import {
  QueuedTodo,
  UpdateReferenceTypes,
} from '@debbie/common/dist/updates/index.js'
import { SocketEvent } from '@debbie/common/dist/web-sockets/index.js'
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js'
import { useCase } from '@debbie/cortex/dist/cases/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { useCustomer } from '@debbie/cortex/dist/customers/index.js'
import { h } from '@debbie/cortex/dist/index.js'
import { useCaseFilter } from '@debbie/cortex/dist/insights/caseFilter.js'
import { usePaymentPlansByCaseId } from '@debbie/cortex/dist/paymentPlans/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import {
  subscribeToEvent,
  unsubscribeFromEvent,
} from '@debbie/cortex/dist/socket.js'
import { useUpdateTypesMap } from '@debbie/cortex/dist/update-types/index.js'
import {
  todosKey,
  useTodos,
  useUpdatesByRefId,
} from '@debbie/cortex/dist/updates/index.js'
import CaseLoader from '@debbie/iris/dist/components/CaseLoader.js'
import {
  Content,
  Errors,
  PageBase,
  PlaceholderLoader,
  TopNav,
} from '@debbie/iris/dist/components/index.js'
import Queue from '@debbie/iris/dist/components/TodoQueue/Queue.js'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'

import CaseSummary from '../components/CaseSummary.js'

const TodoQueue = () => {
  const { id, updateTypeId } = useParams<{
    id?: string
    updateTypeId: string
  }>()

  const updateTypesMapLoadable = useUpdateTypesMap()
  const caseFilter = useCaseFilter()
  const creditorId = useSelector((s: RootState) => s.selectedCreditor)
  const lang = useSelector((s: RootState) => s.lang)

  const todos = useTodos(updateTypeId, caseFilter, creditorId)

  const [minimized, setMinimized] = useState<boolean>(false)
  const [loadTimestamp, setLoadTimestamp] = useState<number | null>(null)

  useEffect(() => {
    const eventHandler = (
      referenceId: string,
      updateId: string,
      _updateTypeId: string,
    ) => {
      if (updateTypeId === _updateTypeId) {
        q.mutateCache<QueuedTodo[]>(
          todosKey(updateTypeId, caseFilter),
          (todos) => {
            return todos.filter((u) => u.referenceId !== referenceId)
          },
        )
      }
    }

    subscribeToEvent(SocketEvent.UPDATECONTINUED, eventHandler)

    return () => {
      unsubscribeFromEvent(SocketEvent.UPDATECONTINUED, eventHandler)
    }
  }, [todos])

  const updateTypesMap = h.hasData(updateTypesMapLoadable)
    ? updateTypesMapLoadable.data
    : null
  const updateType = updateTypesMap?.[updateTypeId]

  return (
    <div>
      <TopNav />
      <ContentMoveToLeft moveToLeft={!minimized}>
        <Content>
          <PageBase>
            {h.isError(updateTypesMapLoadable) ? (
              <Errors errors={[updateTypesMapLoadable.error.message]} />
            ) : h.isError(todos) ? (
              <Errors errors={[todos.error.message]} />
            ) : updateType && h.hasData(todos) ? (
              <>
                {(() => {
                  if (!id) {
                    return null
                  }

                  switch (updateType.referenceType) {
                    case UpdateReferenceTypes.CASE:
                      return (
                        <CaseTodo
                          caseId={id}
                          onLoadTimestamp={setLoadTimestamp}
                        />
                      )
                    case UpdateReferenceTypes.CREDITOR:
                      return <h3>{lang.todoQueue.creditorTodosNotSupported}</h3>
                    default:
                      return null
                  }
                })()}
                <Queue
                  updateType={updateType}
                  updates={todos.data}
                  id={id}
                  loadTimestamp={loadTimestamp}
                  onMinimize={(_minimized) => setMinimized(_minimized)}
                />
              </>
            ) : (
              <PlaceholderLoader />
            )}
          </PageBase>
        </Content>
      </ContentMoveToLeft>
    </div>
  )
}

const ContentMoveToLeft = styled.div<{
  moveToLeft?: boolean
}>`
  transition: 0.1s padding;
  ${(props) =>
    props.moveToLeft
      ? `
        @media (min-width: 1500px) {
          padding-right: 255px;
        }
      `
      : ''}
`

export default TodoQueue

const CaseTodo = (props: {
  caseId: string
  onLoadTimestamp: (timestamp: number) => void
}) => {
  const singleCaseLoader = useCase(props.caseId)
  const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null

  const customerLoader = useCustomer(singleCase?.customerId)
  const customer = h.hasData(customerLoader) ? customerLoader.data : null
  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null
  const updatesLoadable = useUpdatesByRefId(singleCase?.caseId)
  const updates = h.hasData(updatesLoadable) ? updatesLoadable.data : null
  const paymentPlansLoadable = usePaymentPlansByCaseId(singleCase?.caseId)
  const paymentPlans = h.hasData(paymentPlansLoadable)
    ? paymentPlansLoadable.data
    : null
  const caseVouchers_ = useCaseVouchersByCase(singleCase?.caseId)
  const caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null

  // Check for errors in any of the data loaders
  if (h.isError(singleCaseLoader)) {
    return <Errors errors={[singleCaseLoader.error.message]} />
  }
  if (h.isError(customerLoader)) {
    return <Errors errors={[customerLoader.error.message]} />
  }
  if (h.isError(creditorLoadable)) {
    return <Errors errors={[creditorLoadable.error.message]} />
  }
  if (h.isError(updatesLoadable)) {
    return <Errors errors={[updatesLoadable.error.message]} />
  }

  const caseIsLoading = !(
    singleCase &&
    creditor &&
    customer &&
    updates &&
    paymentPlans &&
    caseVouchers
  )

  return (
    <>
      <CaseLoader
        noRedirect
        caseId={props.caseId}
        onLoadTimestamp={props.onLoadTimestamp}
      />
      {caseIsLoading ? (
        <PlaceholderLoader />
      ) : (
        <CaseSummary caseId={props.caseId} />
      )}
    </>
  )
}
